import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Fab,
  CircularProgress,
  Backdrop,
  Tooltip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  format,
  addWeeks,
  subWeeks,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  isToday,
  isPast,
  setHours,
  setMinutes,
  isWithinInterval,
  parse,
  startOfDay,
} from 'date-fns';
import { pl, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import BookMeetingDialog from './BookMeetingDialog';
import MeetingDetailsDialog from './MeetingDetailsDialog';
import LoadingSpinner from '../common/LoadingSpinner';
import { calendarService } from '../../services/calendarService';
import { businessConfigService } from '../../services/businessConfigService';
import { isSlotBookable } from '../../utils/slotValidation';
import { findFirstAvailableSlot } from '../../utils/findFirstAvailableSlot';

/**
 * Weekly Calendar - 7-day grid view for desktop
 */
const WeeklyCalendar = ({ customerId, selectedCalendar }) => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'pl' ? pl : enUS;
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [events, setEvents] = useState({});
  const [calendars, setCalendars] = useState([]);
  const [calendarConfig, setCalendarConfig] = useState(null);
  const [activeCalendar, setActiveCalendar] = useState(selectedCalendar || '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBookDialog, setShowBookDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [prefilledDate, setPrefilledDate] = useState(null);
  const [draggedEvent, setDraggedEvent] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const initializedRef = useRef(false);

  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 }); // Monday
  const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 });
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });

  useEffect(() => {
    const fetchCalendars = async () => {
      try {
        const calendarList = await businessConfigService.getBusinessCalendars(customerId);
        setCalendars(calendarList);
        // Set first calendar as active if none selected
        if (!activeCalendar && calendarList.length > 0) {
          setActiveCalendar(calendarList[0].name);
        }
      } catch (err) {
        console.error('Error fetching calendars:', err);
      }
    };

    if (customerId) {
      fetchCalendars();
    }
  }, [customerId]);

  // Fetch calendar config when activeCalendar changes
  useEffect(() => {
    const fetchCalendarConfig = async () => {
      if (activeCalendar) {
        try {
          const calendars = await businessConfigService.getBusinessCalendars(customerId);
          const calendar = calendars.find(cal => cal.name === activeCalendar);
          setCalendarConfig(calendar || {});
          
          // Find and navigate to first available slot ONLY on initial load
          if (!initializedRef.current) {
            const firstAvailableDate = findFirstAvailableSlot(calendar);
            setCurrentWeek(firstAvailableDate);
            initializedRef.current = true;
          }
        } catch (err) {
          console.error('Error fetching calendar config:', err);
          setCalendarConfig({});
        }
      } else {
        setCalendarConfig({});
      }
    };

    if (customerId) {
      fetchCalendarConfig();
    }
  }, [customerId, activeCalendar]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const customersData = await calendarService.getWeekEvents(customerId, currentWeek);
        
        // Organize events by day
        const eventsByDay = {};
        weekDays.forEach(day => {
          eventsByDay[format(day, 'yyyy-MM-dd')] = [];
        });

        // Parse events from nested structure: {customerId: {business_name, calendars: {calendarName: {data}}}}
        Object.entries(customersData).forEach(([custId, customerInfo]) => {
          const calendars = customerInfo.calendars || {};
          
          Object.entries(calendars).forEach(([calendarName, data]) => {
            if (activeCalendar === calendarName) {
              const calendarEvents = data.calendar_events || [];
              calendarEvents.forEach(event => {
                const parsedEvent = calendarService.parseEvent(event);
                if (parsedEvent) {
                  const eventWithCalendar = {
                    ...parsedEvent,
                    calendarName,
                    customerId: custId,
                    businessName: customerInfo.business_name,
                  };
                  const dayKey = format(parsedEvent.start, 'yyyy-MM-dd');
                  if (eventsByDay[dayKey]) {
                    eventsByDay[dayKey].push(eventWithCalendar);
                  }
                }
              });
            }
          });
        });

        // Sort events within each day
        Object.keys(eventsByDay).forEach(day => {
          eventsByDay[day].sort((a, b) => a.start - b.start);
        });

        setEvents(eventsByDay);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    // Only fetch events if we have customerId, activeCalendar AND calendarConfig
    // This prevents a race condition where events are filtered out before config loads
    if (customerId && activeCalendar && calendarConfig) {
      fetchEvents();
    }
  }, [customerId, currentWeek, activeCalendar, calendarConfig]);

  const handlePreviousWeek = () => {
    setCurrentWeek(subWeeks(currentWeek, 1));
  };

  const handleNextWeek = () => {
    setCurrentWeek(addWeeks(currentWeek, 1));
  };

  const handleToday = () => {
    setCurrentWeek(new Date());
  };

  const handleMeetingClick = (meeting) => {
    setSelectedMeeting(meeting);
    setShowDetailsDialog(true);
  };

  const handleDaySlotClick = (day) => {
    setPrefilledDate(day);
    setShowBookDialog(true);
  };

  const handleTimeSlotClick = (day, timeSlot) => {
    const [hour, minute] = timeSlot.split(':').map(Number);
    const slotDate = setMinutes(setHours(startOfDay(day), hour), minute);
    setPrefilledDate(slotDate);
    setShowBookDialog(true);
  };

  // Generate time slots based on business hours configuration
  const generateTimeSlots = () => {
    const availabilityRules = calendarConfig?.business_hours?.availability_rules;
    const slotDuration = parseInt(calendarConfig?.general?.granularity) || 30;
    
    // Determine the earliest start and latest end across all working days
    let earliestStart = '09:00';
    let latestEnd = '17:00';
    
    if (availabilityRules) {
      earliestStart = availabilityRules.hours_start || '09:00';
      latestEnd = availabilityRules.hours_end || '17:00';
    }
    
    const [startHour, startMin] = earliestStart.split(':').map(Number);
    const [endHour, endMin] = latestEnd.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    
    const slots = [];
    for (let minutes = startMinutes; minutes < endMinutes; minutes += slotDuration) {
      const hour = Math.floor(minutes / 60);
      const min = minutes % 60;
      slots.push(`${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`);
    }
    return slots;
  };

  // Check if a time slot is within business hours for a specific day
  const isWithinBusinessHours = (day, timeSlot) => {
    const availabilityRules = calendarConfig?.business_hours?.availability_rules;
    if (!calendarConfig || !availabilityRules) {
      // Default: Monday-Friday 9-5, weekends closed
      const dayOfWeek = day.getDay();
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        return false; // Sunday or Saturday - closed by default
      }
      
      const [hour, minute] = timeSlot.split(':').map(Number);
      const slotMinutes = hour * 60 + minute;
      return slotMinutes >= 9 * 60 && slotMinutes < 17 * 60; // 9 AM - 5 PM
    }

    const dayOfWeek = day.getDay();
    const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][dayOfWeek].toLowerCase();
    const workingDaysRaw = availabilityRules.days || [];
    const workingDays = workingDaysRaw.map(d => (typeof d === 'string' ? d.toLowerCase() : d));

    // Check if this day is in the working days list (case-insensitive)
    if (!workingDays.includes(dayName)) {
      return false; // Not a working day
    }

    const [hour, minute] = timeSlot.split(':').map(Number);
    const slotMinutes = hour * 60 + minute;

    const hoursStart = availabilityRules.hours_start || '09:00';
    const hoursEnd = availabilityRules.hours_end || '17:00';
    
    const [startHour, startMin] = hoursStart.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;

    const [endHour, endMin] = hoursEnd.split(':').map(Number);
    const endMinutes = endHour * 60 + endMin;

    return slotMinutes >= startMinutes && slotMinutes < endMinutes;
  };

  // Get events for a specific day and time slot
  const getEventsForSlot = (day, timeSlot) => {
    // IMPORTANT: Show events regardless of business hours
    // Events can be booked outside business hours via API
    // The slot itself will be marked as "closed" but events should still display

    const dayKey = format(day, 'yyyy-MM-dd');
    const dayEvents = events[dayKey] || [];
    const slotDuration = parseInt(calendarConfig?.general?.granularity) || 30; // Default to 30 minutes
    
    const [hour, minute] = timeSlot.split(':').map(Number);
    const slotStart = setMinutes(setHours(startOfDay(day), hour), minute);
    const slotEnd = new Date(slotStart.getTime() + slotDuration * 60000);

    return dayEvents.filter(event => {
      // Event overlaps with slot if:
      // 1. Event starts during this slot (event.start >= slotStart && event.start < slotEnd)
      // 2. Slot starts during the event (slotStart >= event.start && slotStart < event.end)
      return (event.start >= slotStart && event.start < slotEnd) ||
             (slotStart >= event.start && slotStart < event.end);
    });
  };

  // Check if event starts in this specific slot (not just overlaps)
  const eventStartsInSlot = (event, day, timeSlot) => {
    const [hour, minute] = timeSlot.split(':').map(Number);
    const slotStart = setMinutes(setHours(startOfDay(day), hour), minute);
    const slotEnd = new Date(slotStart.getTime() + 30 * 60000);
    
    return event.start >= slotStart && event.start < slotEnd;
  };

  // Calculate how many 40px slots this event spans
  const calculateEventHeight = (event) => {
    const durationMinutes = (event.end - event.start) / 60000;
    const slotDuration = parseInt(calendarConfig?.granularity) || 30;
    const slots = Math.ceil(durationMinutes / slotDuration);
    return slots * 40; // 40px per slot
  };

  const handleMeetingUpdated = () => {
    // Refresh events after booking/updating/canceling
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const customersData = await calendarService.getWeekEvents(customerId, currentWeek);
        
        const eventsByDay = {};
        weekDays.forEach(day => {
          eventsByDay[format(day, 'yyyy-MM-dd')] = [];
        });

        // Parse events from nested structure
        Object.entries(customersData).forEach(([custId, customerInfo]) => {
          const calendars = customerInfo.calendars || {};
          Object.entries(calendars).forEach(([calendarName, data]) => {
            if (activeCalendar === calendarName) {
              const calendarEvents = data.calendar_events || [];
              calendarEvents.forEach(event => {
                const parsedEvent = calendarService.parseEvent(event);
                if (parsedEvent) {
                  const eventWithCalendar = {
                    ...parsedEvent,
                    calendarName,
                    customerId: custId,
                    businessName: customerInfo.business_name,
                  };
                  const dayKey = format(parsedEvent.start, 'yyyy-MM-dd');
                  if (eventsByDay[dayKey]) {
                    eventsByDay[dayKey].push(eventWithCalendar);
                  }
                }
              });
            }
          });
        });

        Object.keys(eventsByDay).forEach(day => {
          eventsByDay[day].sort((a, b) => a.start - b.start);
        });

        setEvents(eventsByDay);
      } catch (err) {
        console.error('Error refreshing events:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  };

  // Drag and drop handlers
  const handleDragStart = (event, meeting) => {
    event.stopPropagation();
    setDraggedEvent(meeting);
  };

  const handleDragOver = (event, day, slotTime) => {
    event.preventDefault();
    if (draggedEvent) {
      setDropTarget({ day: format(day, 'yyyy-MM-dd'), time: slotTime });
    }
  };

  const handleDragLeave = () => {
    setDropTarget(null);
  };

  const handleDrop = (event, day, slotTime) => {
    event.preventDefault();
    setDropTarget(null);
    
    if (!draggedEvent || !slotTime) return;

    const [hour, minute] = slotTime.split(':').map(Number);
    const newStartTime = setMinutes(setHours(day, hour), minute);
    
    // Check if the new slot is bookable
    const { isBookable } = isSlotBookable(newStartTime, calendarConfig);
    if (!isBookable) {
      alert('This time slot is not available for booking');
      setDraggedEvent(null);
      return;
    }

    // Open rebook dialog with pre-filled date/time
    setSelectedMeeting(draggedEvent);
    setPrefilledDate(newStartTime);
    setShowBookDialog(true);
    setDraggedEvent(null);
  };

  return (
    <Box>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={handlePreviousWeek}>
              <ChevronLeftIcon />
            </IconButton>
            
            <Typography variant="h6" component="div">
              {format(weekStart, 'MMM d', { locale: dateLocale })} - {format(weekEnd, 'MMM d, yyyy', { locale: dateLocale })}
            </Typography>

            <IconButton onClick={handleNextWeek}>
              <ChevronRightIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<TodayIcon />}
              onClick={handleToday}
              size="small"
              sx={{ height: 40 }}
            >
              {t('common.today')}
            </Button>

            {calendars.length > 1 && (
              <FormControl sx={{ minWidth: 200 }} size="small">
                <InputLabel>Calendar</InputLabel>
                <Select
                  value={activeCalendar}
                  label="Calendar"
                  onChange={(e) => setActiveCalendar(e.target.value)}
                  sx={{ height: 40 }}
                >
                  {calendars.map((cal) => (
                    <MenuItem key={cal.name} value={cal.name}>
                      {cal.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Weekly Time Grid */}
      <Box sx={{ position: 'relative' }}>
        {/* Loading Overlay */}
        {loading && (
          <Backdrop
            open={true}
            sx={{
              position: 'absolute',
              zIndex: (theme) => theme.zIndex.drawer + 1,
              bgcolor: (theme) => theme.palette.mode === 'dark' 
                ? 'rgba(0, 0, 0, 0.7)' 
                : 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(4px)',
            }}
          >
            <CircularProgress />
          </Backdrop>
        )}
        
        {!calendarConfig && !loading ? (
          <LoadingSpinner />
        ) : error ? (
          <Typography color="error" align="center">{error}</Typography>
        ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Grid container spacing={0} sx={{ minWidth: 900 }}>
            {/* Time column header */}
            <Grid item xs={1}>
              <Paper sx={{ 
                p: 1, 
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 64,
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'grey.200',
                borderBottom: 2,
                borderColor: 'divider'
              }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('common.time')}
                </Typography>
              </Paper>
            </Grid>

            {/* Day headers */}
            {weekDays.map((day) => (
              <Grid item xs={(12 - 1) / 7} key={format(day, 'yyyy-MM-dd')}>
                <Paper
                  sx={{
                    p: 1,
                    textAlign: 'center',
                    bgcolor: isToday(day) 
                      ? 'primary.main' 
                      : (theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'grey.200',
                    borderBottom: 2,
                    borderColor: 'divider',
                    color: isToday(day) ? 'primary.contrastText' : 'text.primary'
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={isToday(day) ? 'bold' : 'normal'}>
                    {format(day, 'EEE', { locale: dateLocale })}
                  </Typography>
                  <Typography variant="caption" sx={{ color: isToday(day) ? 'primary.contrastText' : 'text.secondary' }}>
                    {format(day, 'MMM d', { locale: dateLocale })}
                  </Typography>
                </Paper>
              </Grid>
            ))}

            {/* Time slots grid */}
            {generateTimeSlots().map((timeSlot) => (
              <Grid container item xs={12} key={timeSlot} spacing={0}>
                {/* Time label */}
                <Grid item xs={1}>
                  <Paper
                    sx={{
                      p: 0.5,
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderTop: 1,
                      borderColor: 'divider',
                      bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
                    }}
                  >
                    <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      {timeSlot}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Day slots */}
                {weekDays.map((day) => {
                  const slotEvents = getEventsForSlot(day, timeSlot);
                  const slotStartEvents = slotEvents.filter(event => eventStartsInSlot(event, day, timeSlot));
                  const isDayToday = isToday(day);
                  const isBusinessHours = isWithinBusinessHours(day, timeSlot);
                  
                  // Create slot datetime for validation
                  const [hours, minutes] = timeSlot.split(':').map(Number);
                  const slotDateTime = setMinutes(setHours(day, hours), minutes);
                  const { isBookable, reason } = isSlotBookable(slotDateTime, calendarConfig, t);
                  
                  // Check if this slot is covered by an event (but event doesn't start here)
                  const isCoveredByEvent = slotEvents.length > 0 && slotStartEvents.length === 0;
                  
                  return (
                    <Grid item xs={(12 - 1) / 7} key={`${format(day, 'yyyy-MM-dd')}-${timeSlot}`} sx={{ overflow: slotStartEvents.length > 0 ? 'visible' : 'hidden', position: 'relative', zIndex: slotStartEvents.length > 0 ? 1 : 0 }}>
                      <Paper
                        title={isCoveredByEvent ? t('booking.coveredByMeeting') : (!isBookable ? reason : (isBusinessHours ? t('booking.clickToBook') : t('booking.closed')))}
                        sx={{
                          p: (isBusinessHours && isBookable && !isCoveredByEvent) ? 0.5 : 0,
                          height: '40px',
                          position: 'relative',
                          borderTop: 1,
                          borderLeft: 1,
                          borderColor: 'divider',
                          cursor: (isBusinessHours && isBookable && !isCoveredByEvent) ? 'pointer' : 'not-allowed',
                          bgcolor: isCoveredByEvent
                            ? (theme) => theme.palette.mode === 'dark' ? 'grey.850' : 'grey.200'
                            : !isBusinessHours
                            ? (theme) => theme.palette.mode === 'dark' ? 'grey.950' : 'grey.300'
                            : !isBookable
                              ? (theme) => theme.palette.mode === 'dark' ? 'grey.850' : 'grey.200'
                              : slotStartEvents.length > 0 
                                ? (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'action.hover'
                                : (theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper',
                          overflow: slotStartEvents.length > 0 ? 'visible' : 'hidden',
                          '&:hover': {
                            bgcolor: !isBusinessHours
                              ? (theme) => theme.palette.mode === 'dark' ? 'grey.950' : 'grey.300'
                              : !isBookable
                                ? (theme) => theme.palette.mode === 'dark' ? 'grey.850' : 'grey.200'
                                : (theme) => theme.palette.mode === 'dark' ? 'grey.700' : 'action.hover',
                          },
                          ...(dropTarget?.day === format(day, 'yyyy-MM-dd') && dropTarget?.time === timeSlot && isBusinessHours && isBookable && {
                            bgcolor: 'primary.light',
                            opacity: 0.5,
                          }),
                        }}
                        onClick={() => isBusinessHours && isBookable && !isCoveredByEvent && slotStartEvents.length === 0 && handleTimeSlotClick(day, timeSlot)}
                        onDragOver={(e) => handleDragOver(e, day, timeSlot)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, day, timeSlot)}
                      >
                        {slotStartEvents.length === 0 && !isCoveredByEvent && (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', position: 'relative', zIndex: 1 }}>
                            <Typography 
                              variant="caption" 
                              color={(!isBusinessHours || !isBookable) ? 'text.disabled' : 'text.secondary'} 
                              sx={{ fontSize: '0.65rem', opacity: (!isBusinessHours || !isBookable) ? 0.5 : 1 }}
                            >
                              {!isBusinessHours ? t('booking.closed') : !isBookable ? t('booking.unavailable') : t('booking.available')}
                            </Typography>
                          </Box>
                        )}
                        
                        {/* Render events that start in this slot */}
                        {slotStartEvents.length > 0 && (
                          slotStartEvents
                            .filter(event => eventStartsInSlot(event, day, timeSlot))
                            .map((event) => {
                              const eventHeight = calculateEventHeight(event);
                              const durationMinutes = (event.end - event.start) / 60000;
                              const isEventPast = isPast(event.end);
                              const isConfirmed = event.confirmationStatus === 'confirmed';
                              const isPending = event.confirmationStatus === 'pending';
                              const typeLabel = event.typeDescription || event.type || event.summary || 'Meeting';
                              const patientName = event.customerName || '';
                              const timeRange = `${format(event.start, 'h:mm a')} - ${format(event.end, 'h:mm a')}`;
                              const secondLine = patientName ? `${timeRange} ${patientName}` : timeRange;
                              
                              return (
                                <Tooltip
                                  key={event.id}
                                  title={
                                    <Box>
                                      <Typography variant="caption" fontWeight="bold">
                                        {typeLabel}
                                      </Typography>
                                      <Typography variant="caption">
                                        {secondLine}
                                      </Typography>
                                    </Box>
                                  }
                                  arrow
                                  placement="top"
                                >
                                  <Box
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, event)}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMeetingClick(event);
                                    }}
                                    sx={{ 
                                      position: 'absolute',
                                      top: 4,
                                      left: 4,
                                      right: 4,
                                      height: `${eventHeight - 8}px`,
                                      zIndex: 100,
                                      opacity: draggedEvent?.id === event.id ? 0.5 : 1,
                                    }}
                                  >
                                    <Paper
                                      elevation={isEventPast ? 1 : 3}
                                      sx={{
                                        height: '100%',
                                        p: 0.5,
                                        bgcolor: isEventPast
                                          ? (theme) => theme.palette.mode === 'dark' ? 'grey.700' : 'grey.400'
                                          : isConfirmed
                                            ? 'success.main'
                                            : isPending
                                              ? 'warning.main'
                                              : event.isCustomerMeeting
                                                ? 'primary.main'
                                                : 'secondary.main',
                                        color: isEventPast
                                          ? 'text.disabled'
                                          : isConfirmed || isPending
                                            ? 'common.white'
                                            : event.isCustomerMeeting
                                              ? 'primary.contrastText'
                                              : 'secondary.contrastText',
                                        cursor: 'move',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        '&:hover': {
                                          bgcolor: isEventPast
                                            ? (theme) => theme.palette.mode === 'dark' ? 'grey.600' : 'grey.500'
                                            : isConfirmed
                                              ? 'success.dark'
                                              : isPending
                                                ? 'warning.dark'
                                                : event.isCustomerMeeting
                                                  ? 'primary.dark'
                                                  : 'secondary.dark',
                                        },
                                      }}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                                        <Typography variant="caption" noWrap fontWeight="bold" sx={{ fontSize: '0.65rem' }}>
                                          {typeLabel}
                                        </Typography>
                                        {isConfirmed && (
                                          <CheckCircleIcon sx={{ fontSize: '0.9rem' }} />
                                        )}
                                      </Box>
                                      <Typography variant="caption" noWrap display="block" sx={{ fontSize: '0.6rem' }}>
                                        {secondLine}
                                      </Typography>
                                    </Paper>
                                  </Box>
                                </Tooltip>
                              );
                            })
                        )}
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            ))}
          </Grid>
        </Box>
        )}
      </Box>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
        }}
        onClick={() => setShowBookDialog(true)}
      >
        <AddIcon />
      </Fab>

      {/* Dialogs */}
      <BookMeetingDialog
        open={showBookDialog}
        onClose={(success) => {
          setShowBookDialog(false);
          setPrefilledDate(null);
          setSelectedMeeting(null);
          if (success) {
            handleMeetingUpdated();
          }
        }}
        customerId={customerId}
        calendarName={activeCalendar !== 'all' ? activeCalendar : null}
        prefilledDate={prefilledDate}
        existingMeeting={selectedMeeting}
        isRescheduling={!!selectedMeeting}
      />

      <MeetingDetailsDialog
        open={showDetailsDialog}
        onClose={() => {
          setShowDetailsDialog(false);
          setSelectedMeeting(null);
        }}
        meeting={selectedMeeting}
        customerId={customerId}
        onMeetingUpdated={handleMeetingUpdated}
      />
    </Box>
  );
};

export default WeeklyCalendar;
